import {
  Box,
  Flex,
  VStack,
  Stack,
  Center,
  useBreakpointValue,
  useBoolean,
} from '@wegroup/design-system';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

// Components
import SingleOfferDocuments from '../offers/SingleOfferDocuments';
import SingleOfferMainButton from '../offers/SingleOfferMainButton';
import SingleOfferPrice from '../offers/SingleOfferPrice';
import SingleOfferWrapper from '../offers/SingleOfferWrapper';
import SignedOfferGeneralInfo from './SignedOfferGeneralInfo';
import { LoadingSpinner } from 'wg-fe-ui';

// Types
import { toEuro } from '../../utils/stringUtils';
import { mq } from '../../../../common/src/utils/styledComponentsUtils';
import { useQuote } from '../../hooks/useQuoteQueries';
import GeneratingOffer from './GeneratingOffer';
import { createLouiseOffer, getOfferIndex } from '../../services/offersService';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/redux/reducers/rootReducer';
import { LouiseQuote } from '../../../../../types/Quotes';
import OfferFailure from '../offers/OfferFailure';
import { LouiseSessionObject } from '../../../../../types/ProductFactory';
import { isErrorResponse } from '../../../../../types/API';
import { setOfferIdForQuote } from '../../store/redux/slices/offersSlice';
import { useFlowSessionObject } from '../../hooks/useFlowQueries';
import { addMonths, format } from 'date-fns';
import { useLocale } from '../../../../../i18n';
import OfferReportModal from '../offers/OfferReportModal';
import { getGuaranteeNameForKey } from '../../services/productFactoryService';
import cogoToast from 'cogo-toast';
import useSessionId from '../../../../flows/src/hooks/useSessionId';
import useAuth from '../../../../app/src/features/authentication/hooks/useAuth';
import useTracking from '../../../../common/src/hooks/tracking/useTracking';
import Can from '../../../../app/src/features/permissions/Can';
import OfferActionLink from '../OfferActionLink';

interface Props {
  quoteId: string;
  riskObject: string;
}

enum OfferStatus {
  GENERATING,
  DONE,
  ERROR,
  RETRY,
}

const SignedOffer: React.FC<React.PropsWithChildren<Props>> = ({
  quoteId,
  riskObject,
}) => {
  const { t } = useTranslation();
  const sessionId = useSessionId();
  const mobileBreakpoint = useBreakpointValue({ base: false, tablet: true });
  const { userLang } = useLocale();
  const { trackEvent } = useTracking();
  const [isGenerating, setIsGenerating] = useBoolean();

  const dispatch = useDispatch();
  const { user, distribution } = useAuth();

  const offersInfo = useSelector((state: RootState) => state.offers.offersInfo);
  const generatedOffersForQuotes = useSelector(
    (state: RootState) => state.offers.generatedOffersForQuotes,
  );

  const [showReportModal, setShowReportModal] = useState(false);
  const [offerStatus, setOfferStatus] = useState(
    generatedOffersForQuotes[quoteId]
      ? OfferStatus.DONE
      : OfferStatus.GENERATING,
  );

  const { data: quote } = useQuote(riskObject, quoteId);
  const { data: sessionState } = useFlowSessionObject(sessionId);

  useEffect(() => {
    if (quote && sessionState) {
      if (generatedOffersForQuotes[quote.hash_id] == null && !isGenerating) {
        createOfferAsync(quote, sessionState);
      } else {
        setOfferStatus(OfferStatus.DONE);
      }
    }
  }, [quote?.hash_id, sessionState]);

  if (!quote || !sessionState)
    return (
      <StyledSingleOfferWrapper>
        <Center position="absolute" top="0" left="0" w="100%" h="100%">
          <LoadingSpinner />
        </Center>
      </StyledSingleOfferWrapper>
    );

  const contractStartDate =
    offersInfo.contractStartDate[quote.hash_id] || new Date().toISOString();
  const endDate = addMonths(new Date(), 1).toISOString();

  const documents = [
    {
      label: t('TERMS'),
      url: quote?.info.terms || '',
    },
    {
      label: t('IPID'),
      url: quote?.info.ipid || '',
    },
  ];

  const createOfferAsync = async (
    quote: LouiseQuote,
    sessionState: LouiseSessionObject,
  ) => {
    setIsGenerating.on();
    const [resp, status] = await createLouiseOffer(
      quote,
      sessionState,
      distribution,
      userLang,
      user,
    );

    if (status > 201) {
      setOfferStatus(OfferStatus.ERROR);
    } else if (!isErrorResponse(resp)) {
      dispatch(
        setOfferIdForQuote({ quoteId: quote.hash_id, offerId: resp?.offer_id }),
      );
      setOfferStatus(OfferStatus.DONE);
    }
    setIsGenerating.off();
  };

  const signUrl = `${
    process.env.REACT_APP_API_HOST
  }v1/api/broker/v1/api/distributions/${distribution.id}/offers/${
    generatedOffersForQuotes[quote.hash_id]
  }/revisions/0/signatures`;

  const signOfferClickHandler = () => {
    trackEvent('Sign offer in Sales Conversation');
    window.open(signUrl, '_blank');
  };

  const onTryAgain = () => {
    setOfferStatus(OfferStatus.RETRY);
    createOfferAsync(quote, sessionState);
  };

  const onCopy = () => {
    navigator.clipboard.writeText(signUrl);
    cogoToast.success(t('Copied to clipboard'));
  };

  const offerIndex = getOfferIndex(quote.hash_id, riskObject);

  return (
    <StyledSingleOfferWrapper>
      {offerStatus === OfferStatus.GENERATING && <GeneratingOffer />}

      {(offerStatus === OfferStatus.ERROR ||
        offerStatus === OfferStatus.RETRY) && (
        <OfferFailure
          message={t('Error while generating offers')}
          onTryAgain={onTryAgain}
          onReport={() => setShowReportModal(true)}
          isFetching={offerStatus === OfferStatus.RETRY}
        />
      )}
      <OfferReportModal
        showReportModal={showReportModal}
        setShowReportModal={setShowReportModal}
        offer={quote}
      />
      <Stack
        direction={{ base: 'column', tabletS: 'row', tablet: 'column' }}
        mr={8}
        my={6}
        pl={{ base: '2.4rem', tablet: '0' }}
        spacing={6}
        {...(mobileBreakpoint && {
          divider: (
            <Box width="100%" borderBottom="1px" borderColor="gray.100" />
          ),
        })}
      >
        <SignedOfferGeneralInfo
          insuranceCompanyKey={quote.info.company.key}
          insuranceCompanyName={
            quote.info.company?.name ||
            (offerIndex ? `${t('Insurer')} ${offerIndex}` : t('Insurer'))
          }
          insuranceName={getGuaranteeNameForKey(
            quote.info.insurance_name,
            quote.guarantee_content,
          )}
          contractStartDate={format(new Date(contractStartDate), 'dd-MM-yyyy')}
          offerValidDate={format(new Date(endDate), 'dd-MM-yyyy')}
        />
        <StyledSingleOfferDocuments
          title={t('Documents')}
          documents={documents}
        />
      </Stack>
      <Flex
        flexDirection="column"
        borderLeft={{ base: 'none', tablet: '1px' }}
        borderTop={{ base: '1px' }}
        borderColor={{ base: 'gray.100', tablet: 'gray.100' }}
      >
        <VStack justifyContent="center" flexGrow={1} align="center">
          <StyledSingleOfferPrice
            interval={t(
              `spread.${quote.details.payment_interval.toUpperCase()}`,
            )}
          >
            {toEuro(quote.premium.total_premium)}
          </StyledSingleOfferPrice>
        </VStack>
        <Can I="use" a="digital_signatures">
          <OfferActionLink onClick={onCopy}>
            {t('Copy the link for signing this offer')}
          </OfferActionLink>
          <SingleOfferMainButton
            borderBottomLeftRadius={{ base: 'sm', laptop: 0 }}
            onClick={signOfferClickHandler}
            isLoading={isGenerating}
          >
            {t('Sign this offer')}
          </SingleOfferMainButton>
        </Can>
      </Flex>
    </StyledSingleOfferWrapper>
  );
};

const StyledSingleOfferPrice = styled(SingleOfferPrice)`
  flex-direction: row;
  align-items: center;
  padding: 0.8rem;
  margin-top: auto;

  > p:first-child {
    font-size: 1.8rem;
  }

  ${mq.tablet} {
    padding: 0;
    flex-direction: column;
    align-items: flex-start;

    > p:first-child {
      margin-right: 0;
      font-size: 1.4rem;
    }
  }
`;

const StyledSingleOfferDocuments = styled(SingleOfferDocuments)`
  width: 100%;
  margin: 0;

  > h3 {
    margin-right: 3.2rem;
  }

  > ul > li {
    margin-right: 3.2rem;
    margin-bottom: 0;
    &:last-child {
      margin-right: 0;
    }
  }

  ${mq.tabletS} {
    width: 50%;
  }

  ${mq.tablet} {
    width: 100%;
  }
`;

const StyledSingleOfferWrapper = styled(SingleOfferWrapper)`
  position: relative;
  width: 100%;
  grid-template-columns: 1fr;

  ${mq.tablet} {
    padding-left: 2.4rem;
    grid-template-columns: 1fr 22.4rem;
  }
`;

export default SignedOffer;
